import { useParams } from 'react-router-dom'
import { useQuery } from '@connectrpc/connect-query'
import { Trans, useLingui } from '@lingui/react/macro'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { useBrandName, useFeatures } from '@jotta/grpc-connect-client/config'
import { getCurrentCountry } from '@jotta/grpc-connect-openapi/geoQuery'
import { ReferralCodeStatus } from '@jotta/grpc-connect-openapi/referral'
import {
  getMaxReferralBonus as getMaxReferralBonusDescriptor,
  validateReferralCode as validateReferralCodeDescriptor,
} from '@jotta/grpc-connect-openapi/referralQuery'
import { useLocale } from '@jotta/i18n'
import { BaseErrorPage } from '@jotta/ui/BaseErrorPage'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { Redirect } from '@jotta/ui/Redirect'
import SvgFillArrowsDownUp from '@jotta/ui/SvgFillArrowsDownUp'
import SvgFillCloudUpload from '@jotta/ui/SvgFillCloudUpload'
import SvgFillPhotoSearch from '@jotta/ui/SvgFillPhotoSearch'
import { formatPriceConnect } from '@jotta/utils/formatPrice'

import { PublicLayout } from '../components/Layout/PublicLayout'

import { CreateUser } from './CreateUser'

function Features({ brandName }: { brandName: string | undefined }) {
  const { t } = useLingui()
  const features = [
    {
      icon: SvgFillCloudUpload,
      title: t`Automatic backup`,
      description: t`Everything is automatically uploaded to ${brandName} – safely and without hassle.`,
    },
    {
      icon: SvgFillPhotoSearch,
      title: t`Find your photos quickly`,
      description: t`${brandName}'s AI photo search makes it easy and fun to find exactly the photo you're looking for.`,
    },
    {
      icon: SvgFillArrowsDownUp,
      title: t`Easy sharing`,
      description: t`Share files and photos you've uploaded to your cloud with just a few clicks`,
    },
  ]

  return (
    <div className="flex flex-col gap-12">
      {features.map((feature, index) => (
        <div className="grid grid-cols-[50px_auto] gap-6" key={index}>
          <div className="flex items-center justify-center">
            <PlainBrandIcon icon={feature.icon} width={50} height={50} />
          </div>
          <div>
            <h3 className="text-2xl font-normal text-gray-800">
              {feature.title}
            </h3>
            <p className="leading-relaxed text-gray-600">
              {feature.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

export function ReferralRoute() {
  const { referralCode } = useParams()
  const brandName = useBrandName()
  const isAuthenticated = useAuthenticated() === 'TRUE'
  const locale = useLocale()
  const { referralsEnabled, isPending: isLoadingFeatures } = useFeatures()
  const { data: currentCountry } = useQuery(
    getCurrentCountry,
    {},
    {
      throwOnError: false,
      select: data => data.country,
    },
  )
  const { data: maxReferralBonus } = useQuery(
    getMaxReferralBonusDescriptor,
    {
      country: currentCountry,
    },
    {
      select: data => data.maxBonus,
      enabled: currentCountry !== undefined,
    },
  )
  const { data: referralCodeStatus, isPending: isLoadingReferralCodeStatus } =
    useQuery(
      validateReferralCodeDescriptor,
      {
        code: referralCode,
      },
      {
        select: data => data.status,
      },
    )

  const maxReferralBonusFormatted =
    maxReferralBonus && formatPriceConnect(maxReferralBonus, locale)

  if (isAuthenticated) {
    return <Redirect to="/web/upgrade" replace />
  } else if (isLoadingFeatures || isLoadingReferralCodeStatus) {
    return <LoadingOverlay id="ReferralRoute" open={true} />
  } else if (!referralsEnabled) {
    return <Redirect to="/" replace />
  } else if (referralCodeStatus !== ReferralCodeStatus.VALID) {
    return (
      <PublicLayout section="referral">
        <BaseErrorPage reportError={undefined} className="h-full">
          Referral code is invalid
        </BaseErrorPage>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout section="referral" className="flex flex-col items-center">
      <div className="title-lg pt-12 text-center">
        <Trans>You have been invited to try {brandName}!</Trans>
      </div>
      <div className="h2 p-8 text-center">
        {maxReferralBonusFormatted !== undefined && (
          <Trans>
            Choose which subscription you want to use, proceed with up to{' '}
            {maxReferralBonusFormatted} as bonus discount!
          </Trans>
        )}
      </div>

      <div className="grid grid-cols-1 gap-12 self-center p-8 md:grid-cols-[minmax(0,450px)_minmax(0,450px)] md:gap-12">
        <Features brandName={brandName} />
        <CreateUser
          className="rounded-lg bg-surface-container-low p-8"
          allowAppPromotion={false}
          type="private"
          federationIntent={{
            case: 'signup',
            value: {},
          }}
          referralCode={referralCode}
          redirectUrl={'/web/upgrade'}
        />
      </div>
    </PublicLayout>
  )
}
